import { useCallback, useEffect, useRef, useState } from 'react';
import {
  assignAgentToDocument,
  attachDocument,
  createDocument,
  createFile,
  deleteDocument,
  unassignAgentFromDocument,
} from '../../../../services';
import { parseFileCreateResponse, validateApiResponse } from '../../../../shared/utils/apiResponse';
import { getDocumentAssignmentExpiresAt } from '../../../../shared/utils/documentAssignmentExpiry';
import { useAgentKnowledgeEnv } from '../../context/knowledge/AgentKnowledgeEnvContext.jsx';
import { useAgentKnowledgeDocuments } from '../../context/knowledge/AgentKnowledgeDocumentsContext.jsx';

const MAX_FILE_SIZE = 100 * 1024 * 1024;

function extractApiErr(err, fallback) {
  const data = err?.response?.data ?? err?.raw?.response?.data;
  if (data && typeof data === 'object') {
    const msgCode = data.msgCode ?? data.msg_code ?? [];
    if (Array.isArray(msgCode) && msgCode[0]) {
      return msgCode[0].description ?? msgCode[0].code ?? fallback;
    }
  }
  return err?.message ?? fallback;
}

/**
 * Upload flow: createDocument → assignAgent → createFile → PUT presigned → attach.
 * Matches ann-web-app useFileUpload; API envelope is already unwrapped by api client.
 */
export function useAgentFileUpload({ onUploadStart } = {}) {
  const fileInputRef = useRef(null);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadErrors, setUploadErrors] = useState([]);
  const removeTimersRef = useRef(new Map());

  const { agent, agentId, userId, tenantId } = useAgentKnowledgeEnv();
  const { addDocument, updateDocument, removeDocument } = useAgentKnowledgeDocuments();

  const generateTempId = useCallback(
    () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    [],
  );

  const updateUploadingFile = useCallback((tempId, updates) => {
    setUploadingFiles((prev) => ({
      ...prev,
      [tempId]: { ...prev[tempId], ...updates },
    }));
  }, []);

  const removeUploadingFile = useCallback((tempId) => {
    setUploadingFiles((prev) => {
      const next = { ...prev };
      delete next[tempId];
      return next;
    });
  }, []);

  useEffect(
    () => () => {
      removeTimersRef.current.forEach((timer) => clearTimeout(timer));
      removeTimersRef.current.clear();
    },
    [],
  );

  const clearErrors = useCallback(() => setUploadErrors([]), []);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadSingleFile = useCallback(
    async (file, tempId) => {
      const vaultUuid = agent?.vaultUuid;
      const resolvedTenant =
        tenantId ||
        (typeof window !== 'undefined' ? localStorage.getItem('workspace_uuid') : null);

      if (!agentId) {
        const errorMessage = 'No agent selected';
        removeDocument(tempId);
        setUploadErrors((prev) => [
          ...prev,
          { fileName: file.name, message: errorMessage, step: 'Initialization', tempId },
        ]);
        return;
      }

      if (!vaultUuid) {
        const errorMessage = 'No vault available';
        removeDocument(tempId);
        setUploadErrors((prev) => [
          ...prev,
          { fileName: file.name, message: errorMessage, step: 'Initialization', tempId },
        ]);
        return;
      }

      if (!userId) {
        const errorMessage = 'Authentication required';
        removeDocument(tempId);
        setUploadErrors((prev) => [
          ...prev,
          { fileName: file.name, message: errorMessage, step: 'Initialization', tempId },
        ]);
        return;
      }

      if (!resolvedTenant) {
        const errorMessage = 'No workspace / tenant';
        removeDocument(tempId);
        setUploadErrors((prev) => [
          ...prev,
          { fileName: file.name, message: errorMessage, step: 'Initialization', tempId },
        ]);
        return;
      }

      let documentUuid = null;

      const rollback = async () => {
        if (documentUuid && agentId) {
          try {
            await unassignAgentFromDocument(documentUuid, agentId);
          } catch {
            /* ignore */
          }
          try {
            await deleteDocument({ document_uuid: documentUuid });
          } catch {
            /* ignore */
          }
        }
        removeDocument(tempId);
      };

      try {
        updateUploadingFile(tempId, { progress: 10, step: 'Creating document...' });

        const createRes = await createDocument({
          name: file.name,
          vault_uuid: vaultUuid,
          created_by: userId,
          owner: userId,
          tenant_id: resolvedTenant,
          type: file.type || 'application/octet-stream',
          url: '',
          metadata: { source: 'file_upload', version: '1.0' },
        });

        const normalizedCreate = validateApiResponse(
          createRes,
          'Failed to create document',
        );
        const body = normalizedCreate.body || {};
        documentUuid = body?.document?.document_uuid;
        const versionUuid = body?.version?.doc_version_uuid;

        if (!documentUuid || !versionUuid) {
          throw new Error('Failed to create document: missing document or version id');
        }

        updateUploadingFile(tempId, { progress: 25, step: 'Assigning to agent...' });

        await assignAgentToDocument({
          document_uuid: documentUuid,
          agent_uuid: agentId,
          expires_at: getDocumentAssignmentExpiresAt(),
          owner: userId,
          created_by: userId,
          granted_by: userId,
        });

        updateUploadingFile(tempId, { progress: 40, step: 'Preparing upload...' });

        const createFileRes = await createFile({
          name: file.name,
          folder_id: `folder-${Date.now()}`,
          tenant_id: resolvedTenant,
          user_id: userId,
          file_type: file.type || 'application/octet-stream',
          status: 'active',
          vault_uuid: vaultUuid,
          document_uuid: documentUuid,
          version_uuid: versionUuid,
        });

        const normalizedFile = validateApiResponse(
          createFileRes,
          'Failed to create file',
        );
        // The id is nested at `body.file.uuid` while `uploadUrl` is flat — see
        // `parseFileCreateResponse`, which owns that shape.
        const { uploadUrl, fileUuid } = parseFileCreateResponse(
          normalizedFile.body ?? createFileRes,
        );

        if (!uploadUrl) {
          throw new Error('Failed to get upload URL');
        }

        if (!fileUuid) {
          throw new Error('Failed to get file UUID');
        }

        updateUploadingFile(tempId, { progress: 55, step: 'Uploading to storage...' });

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Upload to storage failed: HTTP ${uploadResponse.status} ${uploadResponse.statusText}`,
          );
        }

        updateUploadingFile(tempId, { progress: 80, step: 'Attaching document...' });

        await attachDocument({
          file_uuid: fileUuid,
          documentUuid: documentUuid,
          tenantId: resolvedTenant,
        });

        updateUploadingFile(tempId, { progress: 100, step: 'Completed' });

        updateDocument(tempId, {
          uuid: documentUuid,
          name: file.name,
          state: 'active',
          type: file.type,
          created_at: new Date().toISOString(),
        });

        const removeTimer = setTimeout(() => {
          removeTimersRef.current.delete(tempId);
          removeUploadingFile(tempId);
        }, 2000);
        removeTimersRef.current.set(tempId, removeTimer);
      } catch (err) {
        const errorMessage = extractApiErr(err, 'Upload failed');
        await rollback();
        updateUploadingFile(tempId, {
          error: errorMessage,
          progress: 0,
        });
        setUploadErrors((prev) => [
          ...prev,
          { fileName: file.name, message: errorMessage, step: 'Upload', tempId },
        ]);
      }
    },
    [agent, agentId, userId, tenantId, updateDocument, removeDocument, updateUploadingFile, removeUploadingFile],
  );

  const handleFileSelect = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      if (onUploadStart && files.length > 0) {
        onUploadStart();
      }

      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          setUploadErrors((prev) => [
            ...prev,
            {
              fileName: file.name,
              message: 'File exceeds 100 MB limit',
              step: 'Validation',
              tempId: null,
            },
          ]);
          continue;
        }

        const tempId = generateTempId();
        addDocument({
          uuid: tempId,
          name: file.name,
          state: 'loading',
          type: file.type,
          created_at: new Date().toISOString(),
        });

        setUploadingFiles((prev) => ({
          ...prev,
          [tempId]: { name: file.name, progress: 0, error: null },
        }));

        void uploadSingleFile(file, tempId);
      }

      event.target.value = '';
    },
    [addDocument, generateTempId, uploadSingleFile, onUploadStart],
  );

  const isUploading = Object.values(uploadingFiles).some(
    (f) => !f.error && f.progress > 0 && f.progress < 100,
  );

  return {
    fileInputRef,
    uploadingFiles,
    isUploading,
    uploadErrors,
    openFilePicker,
    handleFileSelect,
    clearErrors,
  };
}
